import axios from "axios";
import { BACKEND_URL } from "../utils/envConfig";
import subscriptionHistoryService from "./subscriptionHistoryService";
import { getAllStaff } from "./StaffService";
import { getAllDoctors } from "./doctorService";
import { getAllUserRoles } from "./UserRoleService";

const API_URL = `${BACKEND_URL}/subscription-role-limits`;

/**
 * Get subscription role limits for a subscription type
 * @param {number} subscriptionTypeId - Subscription type ID
 * @returns {Promise<Array>} Array of role limits with role_id and allowed_users
 */
export const getSubscriptionRoleLimits = async (subscriptionTypeId) => {
  try {
    const response = await axios.get(`${API_URL}/subscription-type/${subscriptionTypeId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription role limits:", error);
    throw error;
  }
};

/**
 * Get role ID from role name
 * @param {string} roleName - Role name (e.g., "doctor", "staff", "admin")
 * @returns {Promise<number|null>} Role ID or null if not found
 */
export const getRoleIdByName = async (roleName) => {
  try {
    const roles = await getAllUserRoles();
    const role = roles.find(
      (r) => r.roleName?.toLowerCase() === roleName?.toLowerCase() ||
             r.username?.toLowerCase() === roleName?.toLowerCase()
    );
    return role?.id || null;
  } catch (error) {
    console.error("Error fetching role ID:", error);
    return null;
  }
};

const filterActiveDoctors = (doctors) =>
  (doctors || []).filter((doctor) => {
    const activeStatus = doctor.isActive ?? doctor.is_active;
    const isActive = activeStatus !== 0 && activeStatus !== false;
    const isDoctor =
      doctor.userRole === "doctor" ||
      doctor.userRole === null ||
      doctor.userRole === undefined;
    return isActive && isDoctor;
  });

const filterActiveStaffByRole = (staff, roleName) => {
  const roleNameLower = roleName?.toLowerCase();
  return (staff || []).filter((member) => {
    const roleMatch =
      member.userRole?.toLowerCase() === roleNameLower ||
      member.user_role?.toLowerCase() === roleNameLower;
    const activeStatus = member.isActive ?? member.is_active;
    const isActive = activeStatus !== 0 && activeStatus !== false;
    return roleMatch && isActive;
  });
};

const countUsersByRole = (roleName, { staff = [], doctors = [] } = {}) => {
  const roleNameLower = roleName?.toLowerCase();
  if (roleNameLower === "doctor" || roleNameLower === "surgeon") {
    return filterActiveDoctors(doctors).length;
  }
  return filterActiveStaffByRole(staff, roleName).length;
};

/**
 * Get current user count for a specific role in an organization
 * @param {number} organizationId - Organization ID
 * @param {string} roleName - Role name (e.g., "doctor", "staff", "admin")
 * @param {{ staff?: Array, doctors?: Array }} [preloaded] - Optional pre-fetched lists
 * @returns {Promise<number>} Current user count for the role
 */
export const getCurrentUserCountByRole = async (organizationId, roleName, preloaded = null) => {
  try {
    const roleNameLower = roleName?.toLowerCase();

    if (roleNameLower === "doctor" || roleNameLower === "surgeon") {
      const doctors =
        preloaded?.doctors ??
        (await getAllDoctors({ forceRefresh: preloaded == null }));
      const filteredDoctors = filterActiveDoctors(doctors);
      console.log(
        `[subscriptionRoleLimitsService] Doctor count for org ${organizationId}: ${filteredDoctors.length}`
      );
      return filteredDoctors.length;
    }

    const staff = preloaded?.staff ?? (await getAllStaff());
    const filteredStaff = filterActiveStaffByRole(staff, roleName);
    console.log(
      `[subscriptionRoleLimitsService] Staff count for org ${organizationId}, role ${roleName}: ${filteredStaff.length}`
    );
    return filteredStaff.length;
  } catch (error) {
    console.error("[subscriptionRoleLimitsService] Error counting users by role:", error);
    return 0;
  }
};

/**
 * Check if creating a user with a specific role would exceed subscription limits
 * @param {number} organizationId - Organization ID
 * @param {string} roleName - Role name (e.g., "doctor", "staff", "admin")
 * @param {{ staff?: Array, doctors?: Array }} [preloaded] - Optional pre-fetched lists
 * @returns {Promise<{allowed: boolean, currentCount: number, limit: number, message: string}>}
 */
export const checkRoleLimit = async (organizationId, roleName, preloaded = null) => {
  try {
    // Get active subscription for the organization
    const activeSubscription = await subscriptionHistoryService.getActiveSubscription(organizationId);
    
    // Handle both camelCase and snake_case field names
    const subscriptionTypeId = activeSubscription?.subscriptionTypeId || 
                               activeSubscription?.subscription_type_id ||
                               activeSubscription?.subscriptionType?.id;
    
    if (!activeSubscription || !subscriptionTypeId) {
      // No active subscription - allow creation (or you can restrict this)
      return {
        allowed: true,
        currentCount: 0,
        limit: -1, // -1 means unlimited
        message: "No active subscription found. User creation allowed.",
      };
    }
    
    // Get role limits for this subscription type
    const roleLimits = await getSubscriptionRoleLimits(subscriptionTypeId);
    
    // Get role ID from role name
    const roleId = await getRoleIdByName(roleName);
    
    if (!roleId) {
      console.warn(`Role ID not found for role name: ${roleName}`);
      // If role not found, allow creation (or restrict based on your business logic)
      return {
        allowed: true,
        currentCount: 0,
        limit: -1,
        message: `Role "${roleName}" not found in role limits. User creation allowed.`,
      };
    }
    
    // Find the limit for this role
    const roleLimit = roleLimits.find(limit => limit.roleId === roleId || limit.role_id === roleId);
    
    if (!roleLimit) {
      // No limit defined for this role - allow creation
      return {
        allowed: true,
        currentCount: 0,
        limit: -1,
        message: `No limit defined for role "${roleName}". User creation allowed.`,
      };
    }
    
    // Handle limit properly - 0 is a valid value, so we can't use || operator
    const limit = roleLimit.allowedUsers !== undefined && roleLimit.allowedUsers !== null 
      ? roleLimit.allowedUsers 
      : (roleLimit.allowed_users !== undefined && roleLimit.allowed_users !== null 
          ? roleLimit.allowed_users 
          : -1);
    
    // If limit is -1, it means unlimited
    if (limit === -1) {
      return {
        allowed: true,
        currentCount: 0,
        limit: -1,
        message: `Unlimited users allowed for role "${roleName}".`,
      };
    }
    
    // Get current user count for this role
    const currentCount = await getCurrentUserCountByRole(organizationId, roleName, preloaded);
    
    // If limit is 0, no users are allowed for this role
    if (limit === 0) {
      return {
        allowed: false,
        currentCount: currentCount,
        limit: 0,
        message: `Cannot create user. This subscription does not allow creating ${roleName} users (limit: 0).`,
      };
    }
    
    // Check if adding one more user would exceed the limit
    const wouldExceed = currentCount >= limit;
    
    return {
      allowed: !wouldExceed,
      currentCount: currentCount,
      limit: limit,
      message: wouldExceed
        ? `Cannot create user. Limit of ${limit} ${roleName}(s) reached. Current count: ${currentCount}/${limit}`
        : `User creation allowed. Current count: ${currentCount}/${limit}`,
    };
  } catch (error) {
    console.error("Error checking role limit:", error);
    // On error, allow creation to avoid blocking users (or restrict based on your business logic)
    return {
      allowed: true,
      currentCount: 0,
      limit: -1,
      message: `Error checking role limit: ${error.message}. User creation allowed.`,
    };
  }
};

/**
 * Get all role limits for the current organization's subscription
 * @param {number} organizationId - Organization ID
 * @param {{ staff?: Array, doctors?: Array }} [options] - Reuse already-loaded lists to avoid duplicate API calls
 * @returns {Promise<Array>} Array of role limits with role name, current count, and limit
 */
export const getAllRoleLimits = async (organizationId, options = {}) => {
  try {
    const activeSubscription = await subscriptionHistoryService.getActiveSubscription(organizationId);
    
    // Handle both camelCase and snake_case field names
    const subscriptionTypeId = activeSubscription?.subscriptionTypeId || 
                               activeSubscription?.subscription_type_id ||
                               activeSubscription?.subscriptionType?.id;
    
    if (!activeSubscription || !subscriptionTypeId) {
      return [];
    }
    const roleLimits = await getSubscriptionRoleLimits(subscriptionTypeId);
    const [allRoles, staff, doctors] = await Promise.all([
      getAllUserRoles(),
      options.staff != null ? Promise.resolve(options.staff) : getAllStaff(),
      options.doctors != null
        ? Promise.resolve(options.doctors)
        : getAllDoctors({ forceRefresh: true }),
    ]);
    const preloaded = { staff, doctors };
    
    // Map role limits with role names and current counts (single staff/doctors fetch)
    const limitsWithDetails = roleLimits.map((limit) => {
        const roleId = limit.roleId || limit.role_id;
        const role = allRoles.find(r => r.id === roleId);
        const roleName = role?.roleName || role?.username || "Unknown";
        // Handle limit properly - 0 is a valid value, so we can't use || operator
        const allowedUsers = limit.allowedUsers !== undefined && limit.allowedUsers !== null 
          ? limit.allowedUsers 
          : (limit.allowed_users !== undefined && limit.allowed_users !== null 
              ? limit.allowed_users 
              : -1);
        const currentCount = countUsersByRole(roleName, preloaded);
        
        return {
          roleId: roleId,
          roleName: roleName,
          allowedUsers: allowedUsers,
          currentCount: currentCount,
          remaining: allowedUsers === -1 ? -1 : Math.max(0, allowedUsers - currentCount),
          isLimitReached: allowedUsers !== -1 && currentCount >= allowedUsers,
        };
      });
    
    console.log(`[subscriptionRoleLimitsService] getAllRoleLimits for org ${organizationId}:`, limitsWithDetails);
    
    return limitsWithDetails;
  } catch (error) {
    console.error("Error getting all role limits:", error);
    return [];
  }
};

export default {
  getSubscriptionRoleLimits,
  getRoleIdByName,
  getCurrentUserCountByRole,
  checkRoleLimit,
  getAllRoleLimits,
};

